/**
 * Flow Management Actions
 * Contains action creators for managing flows at a high level
 */

import type { Dispatch, GetState, MergeBehavior } from "../types/types";
import type { ActionTypeUFL } from "../types/actionTypes";
import type { NormalizedFlowData, FlowItem, Flow } from "../types/flowTypes";
import type { ThunkAction } from "../types/types";
import { goToUrl, incAjaxCount, decAjaxCount, requestUserNames, setVar, showModal } from "./actionCreators";
import { flowRelatedRequestStart, flowRelatedRequestCompleted } from "./loadingActions";
import { requestExternalServiceLayoutData } from "./flowControlActions";
import { requestDestinations, requestDestinationsByAccess } from "./exportLayoutActions2";
import { GetTapadDeliveryTemplates, GetFileBasedDeliveryTemplates } from "./companiesActions";
import { getSAATapadDeliveryTemplates } from "./saaAdvertisersActions";
import { requestReportFieldTree, requestFlowFieldInformation, preloadFieldTree } from "./fieldTreeActions";
import { notifyGreen } from "./notifyActions";
import { editUrlForFlowItem, editUrlForFlow, variablesUrlForFlow, reportsUrlForFlow } from "../reducers/flowItems";
import { searchTree } from "../reducers/_folderGeneral";
import { FlowBaseTypes } from "../helpers/constants";
import { push } from "connected-react-router";
import { getFlowItemsForSelectedFlow } from "../reducers/flowItems";
import { request } from "../helpers/httpInterceptor";
import { toJson } from "../helpers/typedHelpers";
import h from "../helpers";
import { SET_FLOW_NAME_SEARCH_OVERLAY, CLEAR_FLOW_NAME_SEARCH_OVERLAY } from "../reducers/flowDesigner";

// One AbortController per flowId so a new SignalR-triggered requestFlow cancels
// any still-pending fetch for the same flow, preventing a queue of stale requests.
const _flowFetchControllers: Map<number, AbortController> = new Map();
/** Folders already requested via lazy flow tree (including empty folders). */
const _lazyFlowFolderIdsRequested: Set<number> = new Set();
/** Abort in-flight flow design name search when the query changes or clears. */
let _flowNameSearchAbort: AbortController | null = null;

/**
 * Updates the flow list in the store with normalized data
 * @param {NormalizedFlowData} normalizedData - Normalized flow data from the server
 * @param {MergeBehavior} mergeBehavior - How to merge the data ("replace", "merge", "merge-overwrite")
 * @param {Array<number>|null} flowClientVariableIdsToDelete - Optional array of variable IDs to delete
 */
export const updateFlowList = (
    normalizedData: NormalizedFlowData,
    mergeBehavior: MergeBehavior = "replace",
    flowClientVariableIdsToDelete: ?Array<number> = null
): ActionTypeUFL => ({
    type: "UPDATE_FLOW_LIST",
    normalizedData,
    mergeBehavior,
    flowClientVariableIdsToDelete,
});

/**
 * Clears the currently selected flow from the store
 */
export const clearSelectedFlow = () => ({
    type: "CLEAR_SELECTED_FLOW",
});

/**
 * Navigates to the edit page for a specific flow item
 * If the flow item is empty, navigates to its parent instead
 * @param {number} flowItemId - The ID of the flow item to edit
 * @param {boolean} isResetFlowControlTab - Whether to reset the flow control tab to 0
 */
export const goEditFlowItem =
    (flowItemId: number, isResetFlowControlTab: boolean = true) =>
    (dispatch: Dispatch, getState: GetState) => {
        // Import these locally to avoid circular dependencies
        const { setNewTabValue, setSelectedFlowItemId } = require("./flowActions");

        const state = getState();
        const flowItemsById = state.flowItems.byId;
        let flowItem: FlowItem = flowItemsById[flowItemId];
        let flowItemEditId: number = flowItemId;

        if (flowItem == null) {
            return;
        }

        if (flowItem.FlowItemType == "empty") {
            const flowRelations = Object.values(state.flowRelations.byId);
            const parentRelation = flowRelations.find(
                x => x.ChildFlowItemId == flowItem.FlowItemId && x.ParentFlowItemId != 0
            );
            if (parentRelation && flowItemsById[parentRelation.ParentFlowItemId]) {
                flowItem = flowItemsById[parentRelation.ParentFlowItemId];
                flowItemEditId = flowItem.FlowItemId;
            }
        }

        if (isResetFlowControlTab) {
            dispatch(setNewTabValue(0));
        }

        dispatch(setSelectedFlowItemId(flowItemEditId)); // This makes FlowItemEdit load faster
        dispatch(goToUrl(editUrlForFlowItem(flowItem.FlowId, flowItemEditId)));
    };

/**
 * Navigates to the edit page for a specific flow
 * @param {number} flowId - The ID of the flow to edit
 */
export const goEditFlow = (flowId: number) => (dispatch: Dispatch) => {
    // Import locally to avoid circular dependencies
    const { requestFlow } = require("./flowActions");

    dispatch(goToUrl(editUrlForFlow(flowId)));
    dispatch(requestFlow(flowId));
};

/**
 * Navigates to the variables page for a specific flow
 * @param {number} flowId - The ID of the flow
 */
export const goVariablesFlow = (flowId: number) => (dispatch: Dispatch) => {
    dispatch(goToUrl(variablesUrlForFlow(flowId)));
};

/**
 * Navigates to the reports page for a specific flow
 * @param {number} flowId - The ID of the flow
 */
export const goReportsFlow = (flowId: number) => (dispatch: Dispatch) => {
    dispatch(goToUrl(reportsUrlForFlow(flowId)));
};

/**
 * Helper function to find the top level flow folder for a user
 * @private
 */
const findTopLevelFlowFolderForUser = (state: any, userId: number) => {
    let folderId = 0;
    const folders: any = Object.values(state.folders.flowFolders);
    const myTopLevelFolders = folders.filter(x => x.userId == userId && x.folderType == "W");
    const companyFolders = folders.filter(x => x.userId == 0 && x.folderType == "W");
    if (myTopLevelFolders.length > 0) {
        return myTopLevelFolders[0].id;
    }
    if (companyFolders.length > 0) {
        companyFolders.forEach(x => {
            x.children.forEach(z => {
                if (z.userId == userId) {
                    folderId = z.id;
                    return folderId;
                }
            });
        });
    }
    return folderId;
};

/**
 * Creates a new flow
 * @param {string} flowName - Optional name for the new flow
 * @param {number} baseType - Optional base type for the flow
 */
export const newFlow = (flowName?: string, baseType?: number) => (dispatch: Dispatch, getState: GetState) => {
    // Import locally to avoid circular dependencies
    const { setSelectedFlowNoUnsavedCheck, addThing } = require("./flowActions");

    const state = getState();
    const userId = state.session.userId;
    const companyId = state.session.companyId;

    //Getting top level folder Id #1483
    const topLevelFolderId = findTopLevelFlowFolderForUser(state, userId);
    const newFlow: Flow = {
        FlowId: 0,
        FlowName: flowName || "New Flow", // 150
        FlowDescription: baseType ? "New Campaign" : "New Description", // 200
        FlowIsActive: true,
        FlowCompanyId: companyId,
        FlowFolderId: topLevelFolderId,
        FlowDateCreated: "", // timestamp
        FlowCreatedBy: userId,
        FlowDateUpdated: "", // timestamp
        FlowUpdatedBy: userId,
        FlowUniverse: 1,
        IsLocked: false,
        IsLockedPermanently: false,
        FlowLastCalculated: null,
        FlowBaseType: baseType ? baseType : FlowBaseTypes.Default,
        hasUnsavedChanges: false, // Local only
        isSaving: 0, // Local only
        isRenaming: false, // Local only
        shouldValidate: false,
        flowItemsRunning: 0, // Calculated, based on flow items IsRunning state
        isLoadingFlowData: false, // Local only
        isSplitEnabled: true, // Should we control this through a flow guide settings? in the db?
        hasUnsavedLayoutChanges: false,
    };
    const callback = id => dispatch(setSelectedFlowNoUnsavedCheck(id));
    return dispatch(addThing("flows", newFlow, callback, false));
};

/**
 * Controls the local shouldValidate flag in the store - only validate when needed
 * @param {number} flowId - The ID of the flow
 * @param {boolean} shouldValidate - Whether the flow should be validated
 */
export const updateFlowShouldValidate =
    (flowId: number, shouldValidate: boolean) => (dispatch: Dispatch, getState: GetState) => {
        // Import locally to avoid circular dependencies
        const { updateAttribute } = require("./flowActions");

        const state = getState();
        const flow: ?Flow = state.flows.byId[state.selected.flow];

        if (flow == null) return;
        dispatch(updateAttribute("flows", flowId, "shouldValidate", shouldValidate));
    };

/**
 * Closes a flow, checking for unsaved changes and showing a warning modal if necessary
 * @param {number} flowId - The ID of the flow to close
 */
export const closeFlow = (flowId: number) => (dispatch: Dispatch, getState: GetState) => {
    // Import locally to avoid circular dependencies
    const { clearAndRedirect } = require("./flowActions");

    const state = getState();
    const flow: ?Flow = state.flows.byId[flowId];
    if (flow && flow.FlowId > 0 && flow.hasUnsavedChanges) {
        dispatch(setVar("loss_of_work_queued_action", clearAndRedirect()));
        dispatch(setVar("loss_of_work_canceled_queued_action", {}));
        dispatch(showModal("LOSS_OF_WORK", { flow }));
    } else {
        dispatch(setVar("loss_of_work_queued_action", {}));
        dispatch(setVar("loss_of_work_canceled_queued_action", {}));
        dispatch(clearAndRedirect());
    }
};

/**
 * Sets the selected flow, checking for unsaved changes and showing a warning modal if necessary
 * @param {number} flowId - The ID of the flow to select
 */
export const setSelectedFlow = (flowId: number) => (dispatch: Dispatch, getState: GetState) => {
    // Import locally to avoid circular dependencies
    const { flowEnsureItemDetailsExist, flowRemoveUnsavedItems } = require("./flowActions");

    const state = getState();
    const flow: ?Flow = state.flows.byId[state.selected.flow];
    if (flow != null && flow.FlowId > 0 && flow.hasUnsavedChanges == true) {
        // Has HasUnsavedChanges
        dispatch(setVar("loss_of_work_queued_action", setSelectedFlowNoUnsavedCheck(flowId)));
        dispatch(setVar("loss_of_work_canceled_queued_action", {}));
        dispatch(showModal("LOSS_OF_WORK", { flow }));
    } else {
        dispatch(setVar("loss_of_work_queued_action", {}));
        dispatch(setVar("loss_of_work_canceled_queued_action", {}));
        dispatch(flowEnsureItemDetailsExist());
        dispatch(setSelectedFlowNoUnsavedCheck(flowId));
        dispatch(flowRemoveUnsavedItems());
    }
};

/**
 * Sets the selected flow without checking for unsaved changes
 * Updates the URL to navigate to the appropriate flow page
 * @param {number} flowId - The ID of the flow to select
 */
export const setSelectedFlowNoUnsavedCheck = (flowId: number) => (dispatch: Dispatch, getState: GetState) => {
    dispatch({ type: "SET_SELECTED_FLOW", flowId, meta: { doNotBatch: true } });
    const state = getState();
    const flow: ?Flow = state.flows.byId[flowId]; // Fixed: use flowId instead of state.selected.flow

    if (!state.router || !state.router.location) {
        return;
    }

    // Check if flow items are loaded, if not request the flow data
    const flowItems = Object.values(state.flowItems.byId).filter((item: any) => item.FlowId === flowId);
    const isFlowDataLoaded = flowItems.length > 0;

    if (flowId > 0 && !isFlowDataLoaded && flow) {
        // Flow exists in list but items not loaded - request full flow data
        dispatch(requestFlow(flowId));
    }

    // For some pages, update the url to include the flowId in it.  Possibly need a better solution
    // if we get more of these?
    const location = state.router.location;

    //If I am already on the page I am supposed to be on, don't do a redirect
    const onFlowsLandingRefresh = !flowId && location.pathname === "/flows/design";
    const onOpenFlowRefresh = location.pathname === `/flows/${flowId}/design`;
    if (onFlowsLandingRefresh || onOpenFlowRefresh) return;

    if (flowId == null) {
        if (location.pathname.includes("/flows")) {
            dispatch(push("/flows/design"));
        } else {
            return;
        }
    }
    if (location.pathname.includes("/flows") && location.pathname.includes("/design") && flowId) {
        if (flow && flow.FlowBaseType && flow.FlowBaseType != 0) {
            let flowItemsForFlow = getFlowItemsForSelectedFlow(state);
            const filterItem = flowItemsForFlow.find(x => x.FlowItemType == "filter");

            if (filterItem) {
                dispatch(push(`/flows/${flowId}/item/${filterItem.FlowItemId}`));
            } else {
                dispatch(push(`/flows/${flowId}/item`));
            }
        } else {
            dispatch(push(`/flows/${flowId}/design`));
        }
    }
};

/**
 * Requests flow data from the server and loads it into the store
 * @param {number} flowId - The ID of the flow to request
 * @param {MergeBehavior} mergeBehavior - How to merge the data (defaults to "merge")
 */
export const requestFlow =
    (flowId: number, mergeBehavior: MergeBehavior = "replace"): ThunkAction =>
    (dispatch: Dispatch, getState: GetState) => {
        // Import locally to avoid circular dependencies
        const {
            loadFlowData,
            updateIsLoadingFlowData,
            requestFlowFieldFilters,
            checkFlowSaveStatus,
            requestFlowLayoutErrors,
            requestFlowCannedReportColumns,
            updateAttribute,
        } = require("./flowActions");

        //If we switch companies to one that doesn't have flows after executing
        //This makes sure that we don't request the information
        //Since the new current company doesn't have the feature
        const state = getState();
        if (!state.session.enabledFeatures.includes("flow")) {
            return;
        }

        if (mergeBehavior == "replace") {
            mergeBehavior = "merge";
        }

        // Cancel any in-flight getflow request for this flowId before issuing a new one.
        // Without this, rapid SignalR notifications pile up as concurrent pending requests.
        if (_flowFetchControllers.has(flowId)) {
            _flowFetchControllers.get(flowId).abort();
        }

        const controller = new AbortController();
        _flowFetchControllers.set(flowId, controller);

        dispatch(incAjaxCount());
        dispatch(updateFlowShouldValidate(flowId, true));

        // only show loading in designer when we're on the flow, else loading in the background
        if (state.selected.flow && state.selected.flow == flowId) {
            dispatch(flowRelatedRequestStart());
        }

        dispatch(updateIsLoadingFlowData(flowId, true));

        if (
            !state.externalServiceLayoutData ||
            (state.externalServiceLayoutData &&
                (state.externalServiceLayoutData.externalServiceFields.length == 0 ||
                    state.externalServiceLayoutData.externalServiceLayouts.length == 0))
        ) {
            dispatch(requestExternalServiceLayoutData(state.session.companyId));
        }

        return request(`/flows/getflow/${flowId}`, { credentials: "same-origin", signal: controller.signal }, dispatch)
            .then(h.checkStatus)
            .then(toJson)
            .then(res => {
                _flowFetchControllers.delete(flowId);
                loadFlowData(res, dispatch, getState, mergeBehavior);

                // Re-read state here instead of using the closure over `state` captured before
                // the fetch. Multiple concurrent requestFlow calls would all see the same stale
                // pre-fetch state and each dispatch redundant static-data fetches.
                const currentState = getState();

                if (!currentState.vars.destinations || (currentState.vars.destinations && currentState.vars.destinations.length == 0)) {
                    dispatch(requestDestinations());
                }

                if (
                    currentState.session.enabledFeatures.includes("export-destination-ui-redesign") &&
                    (!currentState.vars.availableDestinations || !currentState.vars.requestableDestinations)
                ) {
                    dispatch(requestDestinationsByAccess());
                }

                if (
                    !currentState.vars.tapadDeliveryTypes ||
                    (currentState.vars.tapadDeliveryTypes && currentState.vars.tapadDeliveryTypes == 0)
                ) {
                    dispatch(GetTapadDeliveryTemplates(currentState.session.companyId));
                }

                if (
                    !currentState.vars.fileBasedDeliveryTypes ||
                    (currentState.vars.fileBasedDeliveryTypes && currentState.vars.fileBasedDeliveryTypes.length == 0)
                ) {
                    dispatch(GetFileBasedDeliveryTemplates(currentState.session.companyId));
                }

                if (
                    !currentState.vars.saaTapadDeliveryTypes ||
                    (currentState.vars.saaTapadDeliveryTypes && currentState.vars.saaTapadDeliveryTypes == 0)
                ) {
                    dispatch(getSAATapadDeliveryTemplates(currentState.session.companyId));
                }

                if (res.flowReports.length > 0) {
                    dispatch(requestReportFieldTree());
                }

                dispatch(requestFlowFieldFilters(flowId));
                dispatch(checkFlowSaveStatus(flowId));
                dispatch(updateFlowShouldValidate(flowId, false));
                dispatch(updateIsLoadingFlowData(flowId, false));
                dispatch(flowRelatedRequestCompleted());

                // Skip layout validation while flow items are still executing.
                // requestFlowLayoutErrors (ValidateFlowDestinationLayouts) is only meaningful
                // once the flow has finished; calling it mid-run generates noise and extra load.
                // We check flow items directly from a fresh getState() rather than relying on
                // flowItemsRunning, which is a computed field that may not be updated yet by
                // the time we read currentState.
                const stateAfterLoad = getState();
                const hasRunningItems = Object.values(stateAfterLoad.flowItems.byId).some(
                    item => item.FlowId === flowId && item.IsRunning
                );
                if (!hasRunningItems) {
                    dispatch(requestFlowLayoutErrors(flowId));
                    if (Array.isArray(res.flowCannedReports) && res.flowCannedReports.length > 0) {
                        dispatch(requestFlowCannedReportColumns(flowId));
                    }
                }
            })
            .catch(error => {
                _flowFetchControllers.delete(flowId);
                // An aborted request is intentional - a newer request superseded this one.
                if (error && error.name === "AbortError") {
                    dispatch(flowRelatedRequestCompleted());
                    dispatch(updateIsLoadingFlowData(flowId, false));
                    return;
                }
                dispatch(decAjaxCount());
                h.error("Error loading flow.", error);
                dispatch(updateIsLoadingFlowData(flowId, false));
                dispatch(updateAttribute("flows", flowId, "isLoadingFlowData", false));
                dispatch(flowRelatedRequestCompleted());
            }).finally(() => { 
                 dispatch(requestFlowFieldInformation(flowId));
            } );
    };

/**
 * Requests flows data from the server and loads it into the store
 * @param {number|null} flowId - Optional ID of a specific flow to request
 * @param {MergeBehavior} mergeBehavior - How to merge the data (defaults to "replace")
 * @param {boolean} getAllFlowEntities - Whether to get all flow entities (defaults to true)
 */
export const requestFlows =
    (
        flowId: ?number = null,
        mergeBehavior: MergeBehavior = "replace",
        getAllFlowEntities: boolean = true
    ): ThunkAction =>
    (dispatch: Dispatch, getState: GetState) => {
        // Import locally to avoid circular dependencies
        const { loadFlowData } = require("./flowActions");
        const { ToggleIsFlowTreeLoading, SetIsFlowLoadingExcludes } = require("./treeBehaviorActions");

        let url = "/flows/getflows";
        const state = getState();

        const skipBulkGetFlowsForLazyTree =
            flowId == null &&
            getAllFlowEntities === false &&
            state.session.enabledFeatures.includes("fsd-lazy-loading-flowtree");
        if (skipBulkGetFlowsForLazyTree) {
            return Promise.resolve();
        }

        if (flowId != null) {
            url += "/" + flowId;
            if (mergeBehavior == "replace") {
                mergeBehavior = "merge";
            }
        }

        dispatch(incAjaxCount());

        if (!state.treeBehaviors.isFlowTreeLoadingExclude) {
            dispatch(ToggleIsFlowTreeLoading(true));
        }

        if (state.session.isAuthenticated) {
            if(state.session.enabledFeatures.includes("lazy-loading-fieldtree")) {
                dispatch(preloadFieldTree());
            }
            
            return request(url, { credentials: "same-origin" }, dispatch)
                .then(h.checkStatus)
                .then(toJson)
                .then(data => {
                    // Need to load user names for ALL flow owners
                    const userIds = [...new Set(data.flows.map(item => item.FlowCreatedBy))];
                    dispatch(requestUserNames(userIds, "merge"));
                    loadFlowData(data, dispatch, getState, mergeBehavior, getAllFlowEntities);
                    if (flowId == null) {
                        _lazyFlowFolderIdsRequested.clear();
                    }
                    dispatch(ToggleIsFlowTreeLoading(false));
                    if (state.treeBehaviors.isFlowTreeLoadingExclude) {
                        dispatch(SetIsFlowLoadingExcludes(false));
                    }
                })
                .catch(error => {
                    dispatch(decAjaxCount());
                    dispatch(ToggleIsFlowTreeLoading(false));
                    if (state.treeBehaviors.isFlowTreeLoadingExclude) {
                        dispatch(SetIsFlowLoadingExcludes(false));
                    }
                    h.error("Error loading flows.", error);
                });
        }
    };

/**
 * Loads flows for a single folder (lazy flow tree). Merges into existing flows state.
 */
export const requestFlowsForFolder =
    (parentFolderId: number, mergeBehavior: MergeBehavior = "merge"): ThunkAction =>
    (dispatch: Dispatch, getState: GetState) => {
        const { loadFlowData } = require("./flowActions");
        const { AddFlowFolderLoading, RemoveFlowFolderLoading } = require("./treeBehaviorActions");

        if (parentFolderId <= 0) {
            return Promise.resolve();
        }

        const state = getState();
        if (!state.session.enabledFeatures.includes("fsd-lazy-loading-flowtree")) {
            return Promise.resolve();
        }
        if (!state.session.enabledFeatures.includes("flow")) {
            return Promise.resolve();
        }

        if (_lazyFlowFolderIdsRequested.has(parentFolderId)) {
            return Promise.resolve();
        }
        _lazyFlowFolderIdsRequested.add(parentFolderId);

        dispatch(incAjaxCount());
        dispatch(AddFlowFolderLoading(parentFolderId));
        const url = `/flows/getflowsbyparentfolder?parentFolderId=${parentFolderId}`;

        return request(url, { method: "GET", credentials: "same-origin" }, dispatch)
            .then(h.checkStatus)
            .then(toJson)
            .then(data => {
                const flows = data.flows || [];
                const userIds = [...new Set(flows.map((item: Flow) => item.FlowCreatedBy))];
                dispatch(requestUserNames(userIds, "merge"));
                loadFlowData(data, dispatch, getState, mergeBehavior, false);
            })
            .catch(error => {
                _lazyFlowFolderIdsRequested.delete(parentFolderId);
                h.error("Error loading flows for folder.", error);
            })
            .finally(() => {
                dispatch(decAjaxCount());
                dispatch(RemoveFlowFolderLoading(parentFolderId));
            });
    };

/**
 * Clears flow design name-search overlay and cancels any in-flight name search request.
 */
export const clearFlowDesignNameSearch = (): ThunkAction => (dispatch: Dispatch) => {
    if (_flowNameSearchAbort) {
        _flowNameSearchAbort.abort();
        _flowNameSearchAbort = null;
    }
    dispatch({ type: CLEAR_FLOW_NAME_SEARCH_OVERLAY });
};

/**
 * Build a name-search overlay locally from already-loaded `flows.byId`. Used when
 * `fsd-lazy-loading-flowtree` is disabled so we avoid a redundant API round trip
 * (the flow-design page mount already issued a bulk GetFlows).
 */
const _buildLocalFlowNameSearchOverlay = (
    flowsById: { [number]: Flow } | undefined,
    trimmedQuery: string
): { [number]: Flow } => {
    const byId: { [number]: Flow } = {};
    if (!flowsById) {
        return byId;
    }
    const needle = trimmedQuery.toLowerCase();
    for (const id in flowsById) {
        const f = flowsById[id];
        if (
            f &&
            f.FlowId != null &&
            typeof f.FlowName === "string" &&
            f.FlowName.toLowerCase().indexOf(needle) !== -1
        ) {
            byId[f.FlowId] = f;
        }
    }
    return byId;
};

/**
 * Flow design page only (requires `fsd-flow-search-optimized` feature): produce an ephemeral
 * name-search overlay (does not merge into `flows.byId`).
 *  - Lazy flow tree on  → call `/Flows/GetFlowsByName` (full company catalog isn't loaded client-side).
 *  - Lazy flow tree off → derive overlay from already-loaded `flows.byId` (no network call).
 */
export const requestFlowDesignNameSearch =
    (rawQuery: string): ThunkAction =>
    (dispatch: Dispatch, getState: GetState) => {
        const trimmed = (rawQuery || "").trim();
        if (_flowNameSearchAbort) {
            _flowNameSearchAbort.abort();
            _flowNameSearchAbort = null;
        }
        if (!trimmed) {
            dispatch({ type: CLEAR_FLOW_NAME_SEARCH_OVERLAY });
            return Promise.resolve();
        }

        const state = getState();
        const flowSearchOptimized =
            state.session &&
            Array.isArray(state.session.enabledFeatures) &&
            state.session.enabledFeatures.includes("fsd-flow-search-optimized");
        if (!flowSearchOptimized) {
            dispatch({ type: CLEAR_FLOW_NAME_SEARCH_OVERLAY });
            return Promise.resolve();
        }

        const lazyFlowTree = !!(
            state.session &&
            Array.isArray(state.session.enabledFeatures) &&
            state.session.enabledFeatures.includes("fsd-lazy-loading-flowtree")
        );

        if (!lazyFlowTree) {
            const flowsById = state.flows && state.flows.byId ? state.flows.byId : {};
            const byId = _buildLocalFlowNameSearchOverlay(flowsById, trimmed);
            dispatch({
                type: SET_FLOW_NAME_SEARCH_OVERLAY,
                query: trimmed,
                flowsById: byId,
            });
            return Promise.resolve();
        }

        const ac = new AbortController();
        _flowNameSearchAbort = ac;
        const url = `/flows/getflowsbyname?q=${encodeURIComponent(trimmed)}`;
        return request(url, { method: "GET", credentials: "same-origin", signal: ac.signal }, dispatch)
            .then(h.checkStatus)
            .then(toJson)
            .then((data: { flows?: Array<Flow> }) => {
                if (ac.signal.aborted) {
                    return;
                }
                const byId: { [number]: Flow } = {};
                for (const f of data.flows || []) {
                    if (f && f.FlowId != null) {
                        byId[f.FlowId] = f;
                    }
                }
                const userIds = [...new Set((data.flows || []).map((x: Flow) => x.FlowCreatedBy))];
                if (userIds.length) {
                    dispatch(requestUserNames(userIds, "merge"));
                }
                dispatch({
                    type: SET_FLOW_NAME_SEARCH_OVERLAY,
                    query: trimmed,
                    flowsById: byId,
                });
            })
            .catch(error => {
                if (error && error.name === "AbortError") {
                    return;
                }
                h.error("Error searching flows by name.", error);
            })
            .finally(() => {
                if (_flowNameSearchAbort === ac) {
                    _flowNameSearchAbort = null;
                }
            });
    };

/**
 * Checks the flow save status and displays a success icon temporarily
 * @param {number} flowId - The ID of the flow to check
 */
export const checkFlowSaveStatus =
    (flowId: number): ThunkAction =>
    (dispatch: Dispatch, getState: GetState) => {
        // Import locally to avoid circular dependencies
        const { updateAttribute } = require("./flowActions");

        let flow = getState().flows.byId[flowId];
        if (flow != null) {
            if (flow.isSaving != null && flow.isSaving != 0) {
                dispatch(updateAttribute("flows", flowId, "isSaving", 2)); // Show success icon for a few seconds
                setTimeout(() => {
                    if (flow != null && flowId > 0) {
                        // Flow might have been deleted within 5 seconds(Browser Tests)
                        dispatch(updateAttribute("flows", flowId, "isSaving", 0));
                    }
                }, 3500);
            }
        }
    };

/**
 * Checks if the flow company access has changed and reloads if needed
 * @param {number} flowId - The ID of the flow to check
 */
export const checkFlowPermission = (flowId: number) => (dispatch: Dispatch) => {
    request(`/flows/CheckFlowCompanyAccess/${flowId}`, { method: "POST", credentials: "same-origin" }, dispatch)
        .then(h.checkStatus)
        .then(toJson)
        .then(data => {
            if (data.canSwitch) {
                window.location.reload();
            }
        })
        .catch(error => {
            dispatch(decAjaxCount());
            h.error("Error checking flow company.", error);
        });
};

/**
 * Deletes a flow from the server and updates the UI
 * @param {number} flowId - The ID of the flow to delete
 */
export const requestDeleteFlow = (flowId: number) => (dispatch: Dispatch, getState: GetState) => {
    // Import locally to avoid circular dependencies
    const { updateAttribute, removeLoadedFlowGraph } = require("./flowActions");
    const { SetIsFlowLoadingExcludes } = require("./treeBehaviorActions");

    dispatch(incAjaxCount());
    dispatch(updateAttribute("flows", flowId, "hasUnsavedChanges", false));

    const deleteResultsDbUrl =
        "/Flows/DeleteFlow?" +
        h.serialize({
            flowId,
        });

    // Local updates
    if (getState().selected.flow == flowId) {
        dispatch(setSelectedFlowNoUnsavedCheck(-1));
    }
    dispatch(deleteFlow(flowId));

    // Server request
    return request(deleteResultsDbUrl, { method: "POST", credentials: "same-origin" }, dispatch)
        .then(h.checkStatus)
        .then(toJson)
        .then(data => {
            dispatch(decAjaxCount());
            if (!data.result) {
                if (data.error) {
                    h.error(data.error);
                    throw new Error(data.error);
                } else {
                    throw new Error("Error deleting flow.");
                }
            }
            dispatch(notifyGreen("Flow deleted successfully."));
            dispatch(removeLoadedFlowGraph(flowId));
            dispatch(SetIsFlowLoadingExcludes(true));
            // Refresh the tree from header-only GetFlows. loadFlowData ignores item
            // collections this payload omits, so other open flows keep their nodes.
            dispatch(requestFlows());
        })
        .catch(error => {
            dispatch(decAjaxCount());
            dispatch(SetIsFlowLoadingExcludes(false));

            h.error("Error deleting flow", error);
            throw new Error("Error deleting flow.");
        });
};

/**
 * Deletes a flow from the local state
 * @param {number} id - The ID of the flow to delete
 */
export const deleteFlow = (id: number) => (dispatch: Dispatch) => {
    // Import locally to avoid circular dependencies
    const { _deleteThing } = require("./flowActions");

    dispatch(_deleteThing("flows", id));
};

/**
 * Moves a flow to a different folder
 * @param {number} flowId - The ID of the flow to move
 * @param {number} folderId - The ID of the destination folder
 */
export const requestMoveFlowToFolder =
    (flowId: number, folderId: number) => (dispatch: Dispatch, getState: GetState) => {
        // Import locally to avoid circular dependencies
        const { updateAttribute } = require("./flowActions");

        // Optimistic update, first update client side only
        const state = getState();

        if (
            state.session.userId != state.flows.byId[flowId].FlowCreatedBy &&
            !state.flows.byId[flowId].IsLockedPermanently
        ) {
            h.error("Cannot move another user's flow.");
            return;
        }
        dispatch(updateAttribute("flows", flowId, "FlowFolderId", folderId));
        // Then make request to server
        dispatch(incAjaxCount());
        const moveUrl =
            "/Flows/MoveFlow?" +
            h.serialize({
                flowId,
                folderId,
            });

        fetch(moveUrl, { method: "POST", credentials: "same-origin" })
            .then(h.checkStatus)
            .then(toJson)
            .then(() => {
                dispatch(decAjaxCount());
            })
            .catch(error => {
                dispatch(decAjaxCount());
                h.error("Error moving flow", error);
            });
    };

/**
 * Moves multiple flows to a different folder
 * @param {Array<number>} flowIds - The IDs of the flows to move
 * @param {number} folderId - The ID of the destination folder
 */
export const requestMoveFlowsToFolder =
    (flowIds: Array<number>, folderId: number) => (dispatch: Dispatch, getState: GetState) => {
        // Import locally to avoid circular dependencies
        const { updateAttribute, flowMultiSelectionClear } = require("./flowActions");

        const state = getState();
        const userId = state.session.userId;
        const folders = state.folders.flowFolders;
        const flowsById = state.flows.byId;

        const flowsArray: Array<Flow> = Object.values(flowsById);
        const selectedFlows = flowsArray.filter(x => flowIds.includes(x.FlowId));
        const folder = searchTree(folders, folderId);
        if (!folder || !selectedFlows) {
            return;
        }

        if (folder.isHistorical) {
            if (folder.userId != 0 || selectedFlows.filter(x => !x.IsLockedPermanently).length > 0) {
                h.error("Cannot move flows not locked into a historical folder.");
                return;
            }
        } else if (selectedFlows.filter(x => x.FlowCreatedBy != userId || x.IsLockedPermanently).length) {
            h.error("Cannot move another user's flows.");
            return;
        }

        // Optimistic update, first update client side only
        for (const flow of selectedFlows) {
            dispatch(updateAttribute("flows", flow.FlowId, "FlowFolderId", folderId));
        }
        // do the request only for ids that exist in the db
        const idsToUpdate = flowIds.filter(x => x > 0);

        if (idsToUpdate.length <= 0) {
            return;
        }
        dispatch(flowMultiSelectionClear());

        // Then make request to server
        dispatch(incAjaxCount());
        const moveUrl = "/Flows/MoveFlows";

        fetch(moveUrl, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                flowIds: idsToUpdate,
                folderId,
            }),
        })
            .then(h.checkStatus)
            .then(toJson)
            .then(() => {
                dispatch(decAjaxCount());
            })
            .catch(error => {
                dispatch(decAjaxCount());
                h.error("Error moving flows", error);
            });
    };
